import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa'; 
import '../styles/AndhraPradeshPage.css'; 
import ClientBranches from './ClientBranches';
import CompanyProfile1 from './CompanyProfile1';

const AndhraPradeshClientsPage = () => {
  const [visibleCards, setVisibleCards] = useState(0);
  const [counters, setCounters] = useState({
    clients: 0,
    peopleServed: 0,
    locations: 0
  });

  // Refs for Intersection Observer to animate hero text
  const appHeroTitleRef = useRef(null);
  const appHeroBreadcrumbRef = useRef(null);

  // Andhra Pradesh specific client data (only one client as confirmed)
  const andhraPradeshClients = [
    { name: "SUZLON ENERGY LTD.", location: "Anantapur", persons: 2000, logo: "/clients/Suzlon-3.png", industry: "Energy" }
  ];

  // Calculate total people served and unique locations for Andhra Pradesh
  const totalPeopleServed = andhraPradeshClients.reduce((sum, client) => sum + client.persons, 0);
  const totalLocations = [...new Set(andhraPradeshClients.map(client => client.location))].length;

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          clients: Math.floor(andhraPradeshClients.length * progress),
          peopleServed: Math.floor(totalPeopleServed * progress),
          locations: Math.floor(totalLocations * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            clients: andhraPradeshClients.length,
            peopleServed: totalPeopleServed,
            locations: totalLocations
          });
        }
      }, stepTime);
    };
    animateCounters();
  }, [andhraPradeshClients.length, totalPeopleServed, totalLocations]);

  // Animate client cards appearing (will just animate the single card)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visibleCards < andhraPradeshClients.length) {
        setVisibleCards(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [visibleCards, andhraPradeshClients.length]);

  // Callback for Intersection Observer to add/remove animation class
  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("app-animate-in");
      } else {
        entry.target.classList.remove("app-animate-in");
      }
    });
  }, []);

  // Set up Intersection Observers for hero text elements
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentHeroTitleRef = appHeroTitleRef.current;
    const currentHeroBreadcrumbRef = appHeroBreadcrumbRef.current;

    if (currentHeroTitleRef) observer.observe(currentHeroTitleRef);
    if (currentHeroBreadcrumbRef) observer.observe(currentHeroBreadcrumbRef);

    return () => {
      if (currentHeroTitleRef) observer.unobserve(currentHeroTitleRef);
      if (currentHeroBreadcrumbRef) observer.unobserve(currentHeroBreadcrumbRef);
    };
  }, [handleIntersect]);

  return (
    <>
  <Helmet>
      <title>Clients in Andhra Pradesh | Trusted Partner Suzlon Energy | Laxmi Hospitality Services</title>
      <meta
        name="description"
        content="Laxmi Hospitality Services proudly partners with Suzlon Energy Ltd. in Anantapur, Andhra Pradesh, delivering trusted industrial catering services that empower over 2,000 people daily."
      />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href="https://laxmihospitalityservices.in/clients/andhra-pradesh" />
    
      {/* -------- OPEN GRAPH (SOCIAL) -------- */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://laxmihospitalityservices.in/clients/andhra-pradesh" />
      <meta property="og:title" content="Clients in Andhra Pradesh | Trusted Partner Suzlon Energy | Laxmi Hospitality Services" />
      <meta property="og:description" content="Our largest single-location operation in India. Partnering with Suzlon Energy in Anantapur to serve 2,000+ people daily with exceptional industrial catering." />
      <meta property="og:site_name" content="Laxmi Hospitality Services" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:image" content="https://laxmihospitalityservices.in/images/Andhra-pradesh-hero-banner.png" />
    
      {/* -------- ANDHRA PRADESH CLIENTS COLLECTION PAGE -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": "https://laxmihospitalityservices.in/clients/andhra-pradesh",
            "name": "Andhra Pradesh Clients - Laxmi Hospitality Services",
            "description": "Industrial catering partnership in Andhra Pradesh - our largest single-location operation serving renewable energy sector with 2,000+ daily meals.",
            "url": "https://laxmihospitalityservices.in/clients/andhra-pradesh",
            "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
            "isPartOf": {
              "@type": "WebPage",
              "@id": "https://laxmihospitalityservices.in/clients",
              "name": "Our Clients Across India"
            },
            "mainEntity": {
              "@type": "ItemList",
              "name": "Andhra Pradesh Industrial Client",
              "description": "Premier industrial partnership in Andhra Pradesh renewable energy sector",
              "numberOfItems": 1,
              "itemListElement": [
                {
                  "@type": "Organization",
                  "name": "Suzlon Energy Ltd.",
                  "description": "Our largest single-location partnership - renewable energy leader in Anantapur serving 2,000+ people daily",
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": "Anantapur",
                    "addressRegion": "Andhra Pradesh",
                    "addressCountry": "IN"
                  },
                  "numberOfEmployees": {
                    "@type": "QuantitativeValue",
                    "value": 100
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.linkedin.com/company/laxmi-hospitality-services/",
              "https://www.instagram.com/laxmi_hospitality_services/"
            ],
            "inLanguage": "en-IN"
          }
        `}
      </script>
    
      {/* -------- ANDHRA PRADESH STATE BREADCRUMB -------- */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": "https://laxmihospitalityservices.in/#breadcrumb",
            "name": "Website Breadcrumb",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",            "item": "https://laxmihospitalityservices.in/" },
              { "@type": "ListItem", "position": 2, "name": "Clients",         "item": "https://laxmihospitalityservices.in/clients" },
              { "@type": "ListItem", "position": 3, "name": "Andhra Pradesh",  "item": "https://laxmihospitalityservices.in/clients/andhra-pradesh" }
            ]
          }
        `}
      </script>
  </Helmet>

    <div className="app-page-container">
      <section className="app-hero-section">
        <img
          src="/images/Andhra-pradesh-hero-banner.png" 
          alt="Andhra Pradesh Industry Landscape"
          className="app-hero-image"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x600/f0f0f0/333333?text=Andhra+Pradesh+Hero"; }}
        />
        <div className="app-hero-overlay">
          <h2 ref={appHeroTitleRef} className="app-hero-title">Our Clients in Andhra Pradesh</h2>
          <p ref={appHeroBreadcrumbRef} className="app-breadcrumb">
            <a href="/" className="app-breadcrumb-link">Home</a> /
            <a href="/clients" className="app-breadcrumb-link"> Clients</a> / Andhra Pradesh
          </p>
        </div>
      </section>

      {/* Introductory Sentence for Stats Section */}
      <section className="app-intro-stats-section">
        <div className="app-container">
          <p className="app-intro-stats-text">
            Andhra Pradesh stands as a testament to our significant impact, being one of our largest branches where we proudly serve over 2,000 individuals, showcasing the deep trust and expansive reach of our partnerships in the region.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="app-stats-section">
        <div className="app-container">
          <div className="app-stats-grid">
            <div className="app-stat-card">
              <div className="app-stat-number">{counters.clients}</div>
              <div className="app-stat-label">Clients</div>
            </div>
            <div className="app-stat-card">
              <div className="app-stat-number">{counters.peopleServed.toLocaleString()}</div>
              <div className="app-stat-label">People Serving</div>
            </div>
            <div className="app-stat-card">
              <div className="app-stat-number">{counters.locations}</div>
              <div className="app-stat-label">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Client Section */}
      <section className="app-featured-client-section"> 
        <div className="app-container">
          <h2 className="app-clients-section-title">Our Valued Client</h2> 
          <p className="app-clients-section-intro">
           In Andhra Pradesh, our commitment to impactful service is exemplified by our strong partnership with Suzlon Energy Ltd. in Anantapur, a collaboration that underscores our dedication to the region's thriving industries.
          </p>
          <div className="app-featured-client-wrapper">
            {andhraPradeshClients.map((client, index) => (
              <div
                key={index}
                className={`app-client-card-featured ${index < visibleCards ? 'app-visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Client Logo at the Top - Prominent */}
                <div className="app-card-logo-wrapper-featured">
                  <img
                    src={client.logo}
                    alt={`${client.name} Logo`}
                    className="app-card-logo-image-featured"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/250x150/e0e0e0/333333?text=${client.name.split(' ')[0]}`; }}
                  />
                </div>

                {/* Client Info below logo */}
                <div className="app-client-info-v2-featured">
                  <h3 className="app-client-name-v2-featured">{client.name}</h3>
                  <div className="app-location-v2-featured">
                    <FaMapMarkerAlt className="app-icon-v2-featured" />
                    {client.location}
                  </div>
                  <div className="app-people-served-featured">
                    <FaUsers className="app-icon-v2-featured" />
                    {client.persons.toLocaleString()} People Serving
                  </div>
                  <div className="app-type-tag-featured">
                    Type: {client.industry}
                  </div>
                  {/* Highlight specific to this featured client */}
                  <p className="app-client-highlight">
                    This partnership proudly serves over {client.persons.toLocaleString()} individuals, marking it as one of our largest and most trusted engagements in the region.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ClientBranches/>
      <CompanyProfile1/>
    </div>
    </>
  );
};

export default AndhraPradeshClientsPage;
